import * as THREE from "three";

/**
 * Per-sprite distance blur (the HD-2D / Octopath "far things go soft & blocky" look) — WITHOUT any
 * depth buffer. Billboard sprites register here with their sharp base texture; each frame we measure
 * each sprite's real distance to the player and swap its material.map to a pre-blurred TIER of that
 * texture. A tier is just the base image downsampled (averaged → soft) to a small canvas, so far
 * sprites read as chunky soft blobs while near ones stay crisp. Tier textures are cached per base.
 *
 * This sidesteps the depth-of-field post-pass (the depth texture doesn't capture in this WebGL setup).
 */

/** Distance bands (world units) → tier index. < bands[0] = sharp (tier 0); each band bumps a tier. */
const BANDS_DEFAULT = [12, 26, 48];
/** Downsample factor per tier (1 = sharp). Higher = blurrier/blockier. */
const DOWNSCALE_DEFAULT = [1, 3, 6, 11];

interface Entry { sp: THREE.Sprite; base: THREE.Texture; tier: number }

/** Build a blurred tier of `base` by drawing its image into a downsized canvas (smoothing = average
 *  = blur), sampled smoothly. Returns the base unchanged if its image hasn't loaded yet or ds<=1. */
function makeTier(base: THREE.Texture, ds: number): THREE.Texture {
  if (ds <= 1) return base;
  const img = base.image as (HTMLImageElement | HTMLCanvasElement | ImageBitmap | undefined);
  const w = img ? (img.width as number) : 0, h = img ? (img.height as number) : 0;
  if (!img || !w || !h) return base; // not ready — fall back to sharp; re-tried when the tier is next needed
  const sw = Math.max(1, Math.round(w / ds)), sh = Math.max(1, Math.round(h / ds));
  const c = document.createElement("canvas"); c.width = sw; c.height = sh;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = true; // downscale averages neighbouring pixels → a soft blur
  ctx.drawImage(img as CanvasImageSource, 0, 0, sw, sh);
  const t = new THREE.CanvasTexture(c);
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter; // smooth upscale on the sprite
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

class DistanceBlur {
  private readonly entries: Entry[] = [];
  private readonly tierCache = new Map<THREE.Texture, (THREE.Texture | null)[]>();
  private enabled = false;
  bands = [...BANDS_DEFAULT];
  downscale = [...DOWNSCALE_DEFAULT];
  /** Distance-cull radius (world units): registered sprites farther than this from the player are
   *  hidden (incl. background trees / perimeter forest). 0 = no culling. Tunable via the VFX panel. */
  cullRadius = 100;

  /** Register a billboard sprite (its material.map should currently be `base`, the sharp texture). */
  register(sp: THREE.Sprite, base: THREE.Texture): void {
    this.entries.push({ sp, base, tier: 0 });
  }

  /** Live-tuning setters (debug panel). Changing downscales rebuilds the cached tier textures. */
  setBands(b: number[]): void { this.bands = b.slice(); }
  setDownscale(d: number[]): void { this.downscale = d.slice(); this.tierCache.clear(); }

  /** The texture for `base` at tier `i`. Lazily builds + caches the blurred tier; if the base image
   *  isn't loaded yet, returns the sharp base WITHOUT caching, so it's retried (self-healing). */
  private tier(base: THREE.Texture, i: number): THREE.Texture {
    if (i <= 0) return base;
    let arr = this.tierCache.get(base);
    if (!arr) { arr = [base, null, null, null]; this.tierCache.set(base, arr); }
    if (arr[i] != null) return arr[i]!;
    const built = makeTier(base, this.downscale[i] ?? 1);
    if (built === base) return base;  // image not ready — don't cache, retry next frame
    arr[i] = built;
    return built;
  }

  /** When leaving the blurred zone, snap everything back to sharp so other zones aren't affected. */
  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    if (!on) for (const e of this.entries) this.setMap(e, e.base);
  }

  private setMap(e: Entry, tex: THREE.Texture): void {
    // Swap between two non-null map textures only — no `material.needsUpdate` (that forces a shader
    // recompile; the map uniform is re-read each frame, so the reference change is picked up free).
    const mat = e.sp.material as THREE.SpriteMaterial;
    if (mat.map !== tex) mat.map = tex;
  }

  /** Each frame (in the blurred zone): pick each sprite's tier by its distance to the player + apply.
   *  Comparing the live map (not just the tier index) self-heals once a tier's blur finishes building. */
  update(px: number, pz: number): void {
    if (!this.enabled) return;
    for (const e of this.entries) {
      const dx = e.sp.position.x - px, dz = e.sp.position.z - pz, d2 = dx * dx + dz * dz;
      const bands = this.bands; let tier = bands.length;
      for (let i = 0; i < bands.length; i++) if (d2 < bands[i] * bands[i]) { tier = i; break; }
      e.tier = tier;
      this.setMap(e, this.tier(e.base, tier));
    }
  }

  /** Hide registered sprites past `cullRadius` from the player (world units); show the rest. Cheap
   *  per-frame distance check — no draw cost for the hidden ones. Independent of the blur tiers. */
  cull(px: number, pz: number): void {
    const r = this.cullRadius;
    if (r <= 0) { this.uncull(); return; }
    const r2 = r * r;
    for (const e of this.entries) {
      const dx = e.sp.position.x - px, dz = e.sp.position.z - pz;
      e.sp.visible = dx * dx + dz * dz <= r2;
    }
  }

  /** Restore every registered sprite to visible (e.g. when leaving the culled zone). */
  uncull(): void { for (const e of this.entries) e.sp.visible = true; }
}

/** Single shared instance — Kapon scenery registers into it; world3d drives it while in Kapon. */
export const distanceBlur = new DistanceBlur();
